import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import API_BASE from '../../../apiConfig';
import './ResetPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [username, setUsername] = useState('');

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/auth/reset-password/${token}`);
        const data = await response.json();
        if (response.ok) {
          setTokenValid(true);
          setUsername(data.username || '');
        } else {
          setError(data.detail || 'Invalid or expired reset link');
        }
      } catch (err) {
        setError('Failed to validate reset link');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain an uppercase letter');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError('Password must contain a lowercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain a number');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccess(data.message || 'Password has been reset successfully');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="login-wrapper">
        <div className="login-split-container">
          <div className="login-hero-section">
            <div className="login-hero-content">
              <div className="logo-badge">ForeC.AI</div>
              <h2>Next Generation <br/>Supply Chain Intelligence</h2>
              <p>Empowering your organization with prescriptive analytics, scenario modeling, and predictive insights.</p>
              <div className="hero-floating-elements">
                <div className="float-card card-1">
                  <span>Forecast Accuracy</span>
                  <strong>+24%</strong>
                </div>
                <div className="float-card card-2">
                  <span>Optimization</span>
                  <strong>Real-time</strong>
                </div>
              </div>
            </div>
            <div className="hero-overlay"></div>
          </div>
          <div className="login-form-section">
            <div className="login-form-container">
              <div className="login-header">
                <h1 className="login-title">Invalid Link</h1>
                <p className="login-subtitle">This password reset link is invalid.</p>
              </div>
              <div className="auth-footer-link">
                <Link to="/forgot-password">Request a new reset link</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrapper">
      <div className="login-split-container">
        {/* Left Side: Branding */}
        <div className="login-hero-section">
          <div className="login-hero-content">
            <div className="logo-badge">ForeC.AI</div>
            <h2>Next Generation <br/>Supply Chain Intelligence</h2>
            <p>Empowering your organization with prescriptive analytics, scenario modeling, and predictive insights.</p>
            
            <div className="hero-floating-elements">
              <div className="float-card card-1">
                <span>Forecast Accuracy</span>
                <strong>+24%</strong>
              </div>
              <div className="float-card card-2">
                <span>Optimization</span>
                <strong>Real-time</strong>
              </div>
            </div>
          </div>
          <div className="hero-overlay"></div>
        </div>

        {/* Right Side: Reset Password Form */}
        <div className="login-form-section">
          <div className="login-form-container">
            <div className="login-header">
              <h1 className="login-title">Reset Password</h1>
              <p className="login-subtitle">
                {validating ? 'Validating reset link...' : 
                 tokenValid ? `Enter a new password for ${username || 'your account'}` :
                 'Reset link is invalid'}
              </p>
            </div>

            {validating && (
              <div className="reset-loading">
                <div className="spinner"></div>
                <span>Validating reset link...</span>
              </div>
            )}

            {!validating && error && (
              <div className="login-error-alert animate-shake">
                <span className="alert-icon">!</span>
                <span className="alert-message">{error}</span>
              </div>
            )}

            {!validating && success && (
              <div className="forgot-success-alert">
                <span className="alert-icon">✓</span>
                <span className="alert-message">{success}</span>
              </div>
            )}

            {!validating && tokenValid && !success && (
              <form className="login-form" onSubmit={handleSubmit}>
                <div className="input-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 8 chars, upper, lower, number"
                      required
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your new password"
                      required
                    />
                  </div>
                </div>

                <button 
                  type="submit" 
                  className={`btn-primary login-btn ${loading ? 'loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner"></span>
                  ) : (
                    <>
                      <span>Reset Password</span>
                      <span className="btn-icon">→</span>
                    </>
                  )}
                </button>
              </form>
            )}

            <div className="auth-footer-link">
              <Link to="/login">← Back to Sign In</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
